import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import './my_flashcards.css';
import DashboardFooter from '../../components/DashboardFooter';
import DashboardHeader from '../../components/DashboardHeader';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import cacheManager from '../../auth/cache_manager';
import { FaHome, FaSearch, FaCog, FaPlus } from 'react-icons/fa';

// # Import tab components
import FlashcardHome from './tabs/flashcard_home';
import FlashcardBrowser from './tabs/flashcard_browser';
import FlashcardPreferences from './tabs/flashcard_preferences';

// # Import icons from aneo_media
import stepOneIcon from '../../aneo_media/mentee/one.png';
import stepTwoIcon from '../../aneo_media/mentee/two.png';

function MyFlashcards() {
  const { userProfile, user } = useAuthStore();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Steps data for decks
  const steps = [
    {
      id: 'step1',
      title: 'USMLE Step 1',
      icon: stepOneIcon,
      description: 'Basic Sciences and Foundations'
    },
    {
      id: 'step2',
      title: 'USMLE Step 2',
      icon: stepTwoIcon,
      description: 'Clinical Knowledge and Applications'
    }
  ];

  // # Navigation items
  const navigationItems = [
    { 
      id: 'home', 
      icon: <FaHome />,
      label: 'Home',
      path: '/flashcards/home'
    },
    { 
      id: 'browser', 
      icon: <FaSearch />,
      label: 'Browser',
      path: '/flashcards/browser'
    },
    { 
      id: 'preferences', 
      icon: <FaCog />,
      label: 'Preferences',
      path: '/flashcards/preferences'
    }
  ];

  return (
    <div className={`flsh_container ${isDarkMode ? 'flsh_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="flsh_content">
        <div className="flsh_main-container">
          <h1>
            <span>My Flashcards - {userProfile?.firstName || user?.email?.split('@')[0]}</span>
            <span className="flsh_coming-soon">Coming Soon</span>
          </h1>
          
          <div className="flsh_layout">
            {/* # Vertical navigation */}
            <div className="flsh_sidebar">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) => 
                    `flsh_sidebar-link ${isActive ? 'active' : ''}`
                  }
                >
                  <div className="flsh_sidebar-item">
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                </NavLink>
              ))}
            </div>

            {/* # Content area */}
            <div className="flsh_tab-content">
              <Routes>
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<FlashcardHome isDarkMode={isDarkMode} steps={steps} />} />
                <Route path="browser" element={<FlashcardBrowser isDarkMode={isDarkMode} steps={steps} />} />
                <Route path="preferences" element={<FlashcardPreferences isDarkMode={isDarkMode} />} />
                <Route path="*" element={<Navigate to="home" replace />} />
              </Routes>
            </div>
          </div>
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default MyFlashcards;
